import { GoogleGenAI, Type } from "@google/genai";
import { AestheticStyle, ImageSize, StudioSettings, VeoModel, CinematicPlan, ImageModel } from "../types";
import { ANGLE_DESCRIPTIONS, AESTHETIC_PROMPTS, BASE_PHOTOGRAPHY_PROMPT } from "../config/prompts";

// Cache the API key for the session to avoid repeated env lookups or stale values
let sessionApiKey: string | null = null;

/**
 * Helper to safely get the API key from various environments.
 */
const getEnvApiKey = (): string => {
  // 1. Check for standard process.env (Runtime injection often uses this)
  try {
    // @ts-ignore
    if (typeof process !== 'undefined' && process.env?.API_KEY) {
      // @ts-ignore
      return process.env.API_KEY;
    }
  } catch (e) {
    // Ignore
  }

  // 2. Check for Vite environment variable (Build time)
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_KEY) {
    // @ts-ignore
    return import.meta.env.VITE_API_KEY;
  }
  
  return '';
};

const getApiKey = (): string => {
    if (sessionApiKey) return sessionApiKey;
    return getEnvApiKey();
};

/**
 * Helper to validate if API key exists.
 */
export const hasValidApiKey = (): boolean => {
    const key = getApiKey();
    return !!key && key.length > 0;
};

/**
 * Helper to safely stringify error objects for inspection.
 */
const safeErrorString = (error: any): string => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    
    let str = '';
    if (error.message) str += error.message + ' ';
    if (error.name) str += error.name + ' ';
    if (error.status) str += `Status: ${error.status} `;
    if (error.code) str += `Code: ${error.code} `;
    
    // Check for nested error object
    if (error.error) {
        if (typeof error.error === 'string') str += error.error + ' ';
        else {
             if (error.error.message) str += error.error.message + ' ';
             if (error.error.code) str += error.error.code + ' ';
             if (error.error.status) str += error.error.status + ' ';
        }
    }
    
    return str;
};

/**
 * Core wrapper to handle API Key selection, client creation, and error retries.
 */
const executeGenAI = async <T>(
    operation: (ai: GoogleGenAI, apiKey: string) => Promise<T>
): Promise<T> => {
    let apiKey = getApiKey();
    
    // If in AI Studio and no key, force selection
    if (!apiKey && typeof window !== 'undefined' && (window as any).aistudio) {
        try {
            const hasKey = await (window as any).aistudio.hasSelectedApiKey();
            if (!hasKey) {
                 await (window as any).aistudio.openSelectKey();
                 apiKey = getEnvApiKey();
            }
        } catch (e) {
            console.warn("Failed to check/select key via window.aistudio", e);
        }
    }

    if (!apiKey && typeof window !== 'undefined' && (window as any).aistudio) {
         await (window as any).aistudio.openSelectKey();
         apiKey = getEnvApiKey();
    }

    if (!apiKey) {
        throw new Error("API Key is missing. Please select a project with a valid API key.");
    }

    sessionApiKey = apiKey;

    let lastError: any;
    const MAX_ATTEMPTS = 3;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            const ai = new GoogleGenAI({ apiKey });
            return await operation(ai, apiKey);
        } catch (error: any) {
            lastError = error;
            const errStr = safeErrorString(error);
            
            // Handle Permission/Entity Errors (403, 404, etc.)
            const isPermissionError = 
                errStr.includes("403") || 
                errStr.includes("404") || 
                errStr.includes("PERMISSION_DENIED") || 
                errStr.includes("Requested entity was not found") ||
                errStr.includes("NOT_FOUND");

            if (isPermissionError) {
                if (typeof window !== 'undefined' && (window as any).aistudio) {
                    console.log("Permission/Entity Error. Requesting new API key...", errStr);
                    sessionApiKey = null;
                    await (window as any).aistudio.openSelectKey();
                    const newApiKey = getEnvApiKey();
                    if (newApiKey) {
                        apiKey = newApiKey;
                        sessionApiKey = newApiKey;
                        continue;
                    }
                }
                throw error;
            }

            // Handle Transient Errors
            const isTransient = 
                errStr.includes("500") || 
                errStr.includes("503") ||
                errStr.includes("overloaded") ||
                errStr.includes("fetch failed") || 
                errStr.includes("Unexpected end of JSON input") ||
                errStr.includes("ReadableStreamDefaultController");

            if (isTransient) {
                if (attempt < MAX_ATTEMPTS) {
                    const delay = attempt * 1500; 
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                }
            }

            throw error;
        }
    }

    throw lastError;
};

export const enhancePrompt = async (originalPrompt: string): Promise<string> => {
  return executeGenAI(async (ai) => {
      try {
        const prompt = `You are an expert creative director for high-end jewelry brands. Rewrite the following prompt to generate an image that is "Ad-Ready" and optimized for Social Media engagement.
        
        Guidelines:
        - Focus on luxury, desirability, and visual impact.
        - Specify lighting and textures that look professional (e.g., "studio lighting", "8k resolution", "sharp focus").
        - Ensure the description implies a clean composition suitable for Instagram/TikTok ads.
        - Keep it under 50 words.
        
        Input: "${originalPrompt}"`;

        const response = await ai.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: prompt,
        });
        return response.text?.trim() || originalPrompt;
      } catch (error) {
        console.error("Error enhancing prompt:", error);
        return originalPrompt;
      }
  });
};

export const generateVirtualPhotography = async (
  imageBase64: string,
  style: AestheticStyle,
  aspectRatio: string,
  settings?: StudioSettings,
  mimeType: string = 'image/jpeg',
  customInstruction?: string,
  model: string = ImageModel.NANO_BANANA
): Promise<string> => {
  return executeGenAI(async (ai) => {
      // Use prompt from config
      const basePrompt = AESTHETIC_PROMPTS[style] || "Professional product photography, Phase One camera quality, 100mm macro lens.";

      let settingsPrompt = "";
      if (settings && !customInstruction) { 
        const angleDesc = ANGLE_DESCRIPTIONS[settings.camera.angle] || settings.camera.angle;
        settingsPrompt = `
          SPECIFIC CONFIGURATION:
          Lighting: ${settings.lighting.intensity} intensity, ${settings.lighting.temperature} color temperature.
          Background: ${settings.background.color} color, ${settings.background.texture} texture.
          Camera Angle: ${angleDesc}. Depth of Field: ${settings.camera.depthOfField}.
          Props: ${settings.props || "None"}.
        `;
      }

      const fullPrompt = `
        ${BASE_PHOTOGRAPHY_PROMPT}
        
        Aesthetic: ${basePrompt}
        ${settingsPrompt}
        ${customInstruction ? `Custom User Instruction: ${customInstruction}` : ''}
      `;
      
      const config: any = {
        imageConfig: {
             aspectRatio: aspectRatio === '16:9' ? '16:9' : aspectRatio === '9:16' ? '9:16' : aspectRatio === '3:4' ? '3:4' : aspectRatio === '4:3' ? '4:3' : '1:1',
        }
      };

      const response = await ai.models.generateContent({
        model: model,
        contents: {
          parts: [
            { inlineData: { data: imageBase64, mimeType } },
            { text: fullPrompt },
          ],
        },
        config: config
      });

      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) {
          return `data:image/png;base64,${part.inlineData.data}`;
        }
      }
      throw new Error("No image generated");
  });
};

/**
 * Generates a textual storyboard plan for the batch.
 */
const generateStoryboardPlan = async (
    ai: GoogleGenAI,
    imageBase64: string,
    mimeType: string,
    style: string,
    count: number,
    customInstructions: string
): Promise<string[]> => {
    const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: {
            parts: [
                { inlineData: { data: imageBase64, mimeType } },
                { text: `You are a high-end commercial photographer and creative director. 
                Create a ${count}-shot storyboard for a social media campaign featuring this jewelry product.
                
                Aesthetic Style: ${style}
                User Requirements/Props: ${customInstructions || "None provided, use creative judgment matching the style."}
                
                Generate a JSON array of strings. Each string must be a concise, specific visual description for one shot in the sequence (e.g., "Close up on the gem details", "Flatlay with specific props", "On-model lifestyle shot"). 
                Ensure variety in angles and composition while maintaining the ${style} aesthetic.
                The descriptions will be used to generate images.
                
                Example output format: ["Wide shot on marble table with coffee", "Extreme macro of the setting", "Side profile with soft shadows"]` }
            ]
        },
        config: {
            responseMimeType: 'application/json'
        }
    });

    try {
        const plan = JSON.parse(response.text || '[]');
        if (Array.isArray(plan) && plan.length > 0) return plan.slice(0, count);
        return [];
    } catch (e) {
        console.warn("Failed to generate smart plan, using defaults.");
        return [];
    }
};

export const generateBatchPhotography = async (
  imageBase64: string,
  style: AestheticStyle,
  aspectRatio: string,
  count: number,
  settings: StudioSettings,
  mimeType: string,
  onProgress: (c: number) => void,
  model: string = ImageModel.NANO_BANANA,
  customInstruction: string = ''
): Promise<string[]> => {
  return executeGenAI(async (ai) => {
      // 1. Generate the Storyboard Plan first
      let plan: string[] = [];
      try {
          // Combine settings props and custom instruction for the planner
          const fullInstructions = `${settings.props ? `Props: ${settings.props}. ` : ''} ${customInstruction}`;
          plan = await generateStoryboardPlan(ai, imageBase64, mimeType, style, count, fullInstructions);
      } catch (e) {
          console.log("Planning failed, falling back to variations.");
      }

      // If planning returned fewer items than count, fill with generic variations
      while (plan.length < count) {
          plan.push(`Variation ${plan.length + 1}: Creative angle or lighting nuance matching ${style} aesthetic.`);
      }

      const results: string[] = [];
      for (let i = 0; i < count; i++) {
        try {
          if (i > 0) await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Use the specific scene description from the plan
          const sceneDescription = `Storyboard Scene ${i + 1}: ${plan[i]}`;
          
          // Pass the scene description as the custom instruction to the image generator
          // We pass 'undefined' for settings to force the generator to rely on our scene description + base prompt
          const url = await generateVirtualPhotography(
              imageBase64, 
              style, 
              aspectRatio, 
              settings, 
              mimeType, 
              sceneDescription, 
              model
          );
          results.push(url);
          onProgress(i + 1);
        } catch (e: any) {
          console.error(`Batch item ${i} failed`, e);
          const errStr = safeErrorString(e);
          if (errStr.includes("404") || errStr.includes("NOT_FOUND")) {
              throw e;
          }
        }
      }
      return results;
  });
};

export const editImageWithPrompt = async (
  imageBase64: string,
  prompt: string,
  mimeType: string = 'image/jpeg',
  model: string = ImageModel.NANO_BANANA
): Promise<string> => {
  return executeGenAI(async (ai) => {
      const response = await ai.models.generateContent({
        model: model,
        contents: {
          parts: [
            { inlineData: { data: imageBase64, mimeType } },
            { text: `Professional Image Editing Task.
              Instruction: ${prompt}.
              
              CONSTRAINTS:
              1. PRESERVE IDENTITY: The main jewelry piece must remain GEOMETRICALLY and TEXTURALLY IDENTICAL to the original image. Do not hallucinate new details on the product itself.
              2. QUALITY: The result must be a polished, high-end commercial image suitable for social media ads.
              3. REALISM: Photorealistic output, consistent lighting and shadows.` },
          ],
        },
      });

      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) {
          return `data:image/png;base64,${part.inlineData.data}`;
        }
      }
      throw new Error("No image generated");
  });
};

export const generateConceptImage = async (
    prompt: string, 
    size: ImageSize,
    model: string = ImageModel.NANO_BANANA_PRO
): Promise<string> => {
    return executeGenAI(async (ai) => {
        const config: any = {
            imageConfig: {
                aspectRatio: '9:16'
            }
        };

        if (model === ImageModel.NANO_BANANA_PRO) {
            config.imageConfig.imageSize = size;
        }

        const response = await ai.models.generateContent({
            model: model,
            contents: {
                parts: [{ text: `High jewelry design concept for a luxury social media campaign. ${prompt}. Photorealistic, 8k, commercial product photography, perfect lighting and composition.` }]
            },
            config: config
        });
        
        for (const part of response.candidates?.[0]?.content?.parts || []) {
            if (part.inlineData) {
                return `data:image/png;base64,${part.inlineData.data}`;
            }
        }
        throw new Error("No image generated");
    });
};

export const generateCinematicVideo = async (
    prompt: string,
    startImageBase64: string,
    startMimeType: string,
    model: VeoModel,
    endImageBase64?: string,
    endMimeType?: string
): Promise<string> => {
    return executeGenAI(async (ai) => {
        const config: any = {
            numberOfVideos: 1,
            aspectRatio: '9:16', 
        };

        if (model === VeoModel.FAST) {
            config.resolution = '720p';
        }

        const request: any = {
            model: model,
            prompt: `High-end TV Commercial & Social Media Video. ${prompt}.
              Style: Luxury, Viral Aesthetic, Photorealistic, 8k detail.
              Focus: Smooth motion, commercial lighting, perfect product showcase.`,
            image: {
                imageBytes: startImageBase64,
                mimeType: startMimeType
            },
            config: config
        };

        if (endImageBase64 && endMimeType) {
            request.config.lastFrame = {
                imageBytes: endImageBase64,
                mimeType: endMimeType
            };
        }

        let operation = await ai.models.generateVideos(request);
        
        while (!operation.done) {
            await new Promise(resolve => setTimeout(resolve, 5000));
            operation = await ai.operations.getVideosOperation({ operation: operation });
        }

        const videoUri = operation.response?.generatedVideos?.[0]?.video?.uri;
        if (!videoUri) throw new Error("Video generation failed: No URI returned.");

        const videoResponse = await fetch(`${videoUri}&key=${getApiKey()}`);
        if (!videoResponse.ok) throw new Error("Failed to download generated video.");
        
        const blob = await videoResponse.blob();
        return URL.createObjectURL(blob);
    });
};

export const planVideoScenes = async (imageBase64: string, mimeType: string): Promise<CinematicPlan[]> => {
    return executeGenAI(async (ai) => {
        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: {
                parts: [
                    { inlineData: { data: imageBase64, mimeType } },
                    { text: `Analyze this jewelry image. Suggest 3 cinematic video plans for a social media ad.
                      The goal is to create a dynamic video clip.
                      
                      Return a JSON array where each object has:
                      - title (short name)
                      - description (the prompt for the video generator, describing the movement)
                      - editInstruction (instruction to edit the image to create the end frame, e.g. "rotate the ring 90 degrees"). CRITICAL: The edit instruction must imply the product stays the same, only the angle/lighting changes.
                      - icon (one of: 'zoom', 'pan', 'orbit', 'focus')
                      Do not include markdown code blocks.` }
                ]
            },
            config: {
                responseMimeType: 'application/json'
            }
        });

        try {
            return JSON.parse(response.text || '[]');
        } catch (e) {
            console.error("Failed to parse plan JSON", e);
            return [];
        }
    });
};

export const generateMatchingEndFrame = async (imageBase64: string, mimeType: string): Promise<string> => {
     return executeGenAI(async (ai) => {
        const planResponse = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: {
                parts: [
                     { inlineData: { data: imageBase64, mimeType } },
                     { text: "Analyze this jewelry image. Suggest a visual change that keeps the product identical but changes the mood/lighting/angle for a dynamic video transition (e.g. 'change lighting to sunset', 'zoom in slightly'). The result must be derived strictly from the input product. Return just the text instruction." }
                ]
            }
        });
        
        const modificationPrompt = planResponse.text || "Change lighting to golden hour";
        
        return await editImageWithPrompt(imageBase64, modificationPrompt, mimeType);
     });
};